import { useState, useEffect } from "react";
import api from "../api";
import { marcaSync } from "../marca";
import { useTheme } from "../components/Theme";

const ORANGE = "#FF6B2B";

const ETAPAS_PIPELINE = [
  { id: "prospeccao",   label: "Prospecção",   color: "#579bfc" },
  { id: "qualificacao", label: "Qualificação", color: "#3B7FD6" },
  { id: "reuniao",      label: "Reunião",      color: "#8B5CF6" },
  { id: "proposta",     label: "Proposta",     color: "#EE6A1A" },
  { id: "negociacao",   label: "Negociação",   color: "#EBA13A" },
  { id: "ganho",        label: "Ganho",        color: "#1D9E75" },
  { id: "perdido",      label: "Perdido",      color: "#ef4444" },
];

const STAGES_KANBAN = [
  { id: "prospeccao",   label: "Prospecção",   color: "#579bfc" },
  { id: "qualificacao", label: "Qualificação", color: "#3B7FD6" },
  { id: "reuniao",      label: "Reunião",      color: "#8B5CF6" },
  { id: "proposta",     label: "Proposta",     color: "#EE6A1A" },
  { id: "negociacao",   label: "Negociação",   color: "#EBA13A" },
  { id: "ganho",        label: "Ganho",        color: "#1D9E75" },
  { id: "perdido",      label: "Perdido",      color: "#ef4444" },
];

function fmt(v) { return `R$${Number(v||0).toLocaleString("pt-BR")}`; }

// ── GRÁFICO DE PIZZA (SVG) ───────────────────────────────
function GraficoPizza({ dados, titulo, campo="valor" }) {
  const { theme } = useTheme();
  const [hover, setHover] = useState(null);
  const total = dados.reduce((a,d)=>a+d[campo],0)||1;
  const cx=140, cy=140, r=110, ri=55;
  const circ = 2*Math.PI*r;

  let offset = -0.25; // começa do topo
  const slices = dados.filter(d=>d[campo]>0).map(d=>{
    const pct  = d[campo]/total;
    const startAngle = offset * 2 * Math.PI;
    const endAngle   = (offset+pct) * 2 * Math.PI;
    const x1 = cx + r*Math.cos(startAngle);
    const y1 = cy + r*Math.sin(startAngle);
    const x2 = cx + r*Math.cos(endAngle);
    const y2 = cy + r*Math.sin(endAngle);
    const xi1 = cx + ri*Math.cos(startAngle);
    const yi1 = cy + ri*Math.sin(startAngle);
    const xi2 = cx + ri*Math.cos(endAngle);
    const yi2 = cy + ri*Math.sin(endAngle);
    const large = pct > 0.5 ? 1 : 0;
    const path = `M ${xi1} ${yi1} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${ri} ${ri} 0 ${large} 0 ${xi1} ${yi1} Z`;
    const midAngle = (offset+pct/2) * 2 * Math.PI;
    const lx = cx + (r+20)*Math.cos(midAngle);
    const ly = cy + (r+20)*Math.sin(midAngle);
    offset += pct;
    return { ...d, pct, path, lx, ly };
  });

  const hoverItem = hover!==null ? slices[hover] : null;

  return (
    <div>
      <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:16 }}>{titulo}</div>
      <div style={{ display:"flex", alignItems:"center", gap:24, flexWrap:"wrap" }}>
        <svg width={280} height={280} viewBox="0 0 280 280" style={{ flexShrink:0 }}>
          {slices.map((s,i)=>(
            <path key={i} d={s.path} fill={s.color}
              stroke={theme.bgCard} strokeWidth={2}
              opacity={hover===null||hover===i?1:0.5}
              style={{ cursor:"pointer", transition:"opacity .2s, transform .1s" }}
              onMouseEnter={()=>setHover(i)}
              onMouseLeave={()=>setHover(null)}
              transform={hover===i?`translate(${Math.cos((slices[i-1]?.pct||0)*Math.PI)*4||0},0)`:""}
            />
          ))}
          {/* Centro */}
          {hoverItem ? (
            <>
              <text x={cx} y={cy-12} textAnchor="middle" fill={hoverItem.color} fontSize="13" fontWeight="800">{hoverItem.label}</text>
              <text x={cx} y={cy+8}  textAnchor="middle" fill={theme.text}       fontSize="12" fontWeight="700">{campo==="valor"?fmt(hoverItem[campo]):hoverItem[campo]}</text>
              <text x={cx} y={cy+24} textAnchor="middle" fill={theme.textMuted}  fontSize="11">{Math.round(hoverItem.pct*100)}%</text>
            </>
          ) : (
            <>
              <text x={cx} y={cy-6}  textAnchor="middle" fill={theme.text}      fontSize="20" fontWeight="800">{dados.reduce((a,d)=>a+d[campo],0)||0}</text>
              <text x={cx} y={cy+12} textAnchor="middle" fill={theme.textMuted} fontSize="11">total</text>
            </>
          )}
        </svg>

        {/* Legenda */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", gap:8 }}>
          {slices.map((s,i)=>(
            <div key={i} onMouseEnter={()=>setHover(i)} onMouseLeave={()=>setHover(null)}
              style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 10px", borderRadius:8, cursor:"pointer", background:hover===i?s.color+"18":"transparent", transition:"background .15s" }}>
              <div style={{ width:12, height:12, borderRadius:3, background:s.color, flexShrink:0 }}/>
              <span style={{ fontSize:13, color:hover===i?s.color:theme.textSub, fontWeight:hover===i?600:400, flex:1 }}>{s.label}</span>
              <span style={{ fontSize:13, color:s.color, fontWeight:700 }}>{campo==="valor"?fmt(s[campo]):s[campo]}</span>
              <span style={{ fontSize:11, color:theme.textMuted }}>{Math.round(s.pct*100)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── GRÁFICO DE BARRAS HORIZONTAL ─────────────────────────
function GraficoBarrasH({ dados, titulo, campo="valor" }) {
  const { theme } = useTheme();
  const max = Math.max(...dados.map(d=>d[campo]),1);
  return (
    <div>
      <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:16 }}>{titulo}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {dados.map((d,i)=>(
          <div key={i}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:5 }}>
              <span style={{ color:theme.textSub, fontWeight:500 }}>{d.label}</span>
              <span style={{ color:d.color, fontWeight:700 }}>{campo==="valor"?fmt(d[campo]):d[campo]} <span style={{ color:theme.textMuted }}>({Math.round((d[campo]/max)*100)}%)</span></span>
            </div>
            <div style={{ background:theme.bgInput, borderRadius:99, height:14, overflow:"hidden" }}>
              <div style={{ width:`${(d[campo]/max)*100}%`, background:`linear-gradient(90deg,${d.color}CC,${d.color})`, height:"100%", borderRadius:99, transition:"width .6s ease", display:"flex", alignItems:"center", paddingLeft:8 }}>
                {d[campo]/max > 0.15 && <span style={{ fontSize:10, color:"#fff", fontWeight:700 }}>{d.count||d[campo]}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── GRÁFICO DE BARRAS VERTICAL ───────────────────────────
function GraficoBarrasV({ dados, titulo, campo="valor" }) {
  const { theme } = useTheme();
  const [hover, setHover] = useState(null);
  const max = Math.max(...dados.map(d=>d[campo]),1);
  return (
    <div>
      <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:16 }}>{titulo}</div>
      <div style={{ display:"flex", alignItems:"flex-end", gap:8, height:160, paddingBottom:24, position:"relative" }}>
        {/* Linhas de grade */}
        {[0,25,50,75,100].map(p=>(
          <div key={p} style={{ position:"absolute", left:0, right:0, bottom:`${p}px`, borderTop:`1px dashed ${theme.border}44`, pointerEvents:"none" }}/>
        ))}
        {dados.map((d,i)=>{
          const h = Math.max((d[campo]/max)*130, 4);
          return (
            <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}
              onMouseEnter={()=>setHover(i)} onMouseLeave={()=>setHover(null)}>
              {hover===i&&(
                <div style={{ background:"#2C2C2F", border:`1px solid ${d.color}44`, borderRadius:8, padding:"6px 10px", fontSize:11, color:"#F2F2F7", textAlign:"center", whiteSpace:"nowrap", marginBottom:4 }}>
                  <div style={{ fontWeight:700, color:d.color }}>{d.label}</div>
                  <div>{campo==="valor"?fmt(d[campo]):d[campo]}</div>
                </div>
              )}
              <div style={{ width:"100%", height:h, background:`linear-gradient(180deg,${d.color},${d.color}CC)`, borderRadius:"6px 6px 0 0", cursor:"pointer", opacity:hover===null||hover===i?1:0.5, transition:"all .2s" }}/>
              <div style={{ fontSize:9, color:theme.textMuted, textAlign:"center", marginTop:4, lineHeight:1.2 }}>{d.label.split(" ")[0]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── FUNIL VISUAL ─────────────────────────────────────────
function GraficoFunil({ dados, titulo }) {
  const { theme } = useTheme();
  const max = Math.max(...dados.map(d=>d.valor),1);
  const funil = dados.filter(d=>d.id!=="perdido").sort((a,b)=>b.valor-a.valor);
  return (
    <div>
      <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:16 }}>{titulo}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
        {funil.map((d,i)=>{
          const pct = Math.max((d.valor/max)*100, 15);
          return (
            <div key={d.id} style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
              <div style={{ width:`${pct}%`, background:`linear-gradient(90deg,${d.color}CC,${d.color})`, borderRadius:i===0?"8px 8px 0 0":i===funil.length-1?"0 0 8px 8px":"0", padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", transition:"width .6s ease" }}>
                <span style={{ fontSize:12, color:"#fff", fontWeight:600 }}>{d.label}</span>
                <span style={{ fontSize:12, color:"#fff", fontWeight:700 }}>{fmt(d.valor)}</span>
              </div>
              {i<funil.length-1&&<div style={{ width:0, height:0, borderLeft:"12px solid transparent", borderRight:"12px solid transparent", borderTop:`8px solid ${d.color}` }}/>}
            </div>
          );
        })}
      </div>
      {dados.find(d=>d.id==="perdido")?.valor>0&&(
        <div style={{ marginTop:16, padding:"10px 14px", background:"#ef444418", border:"1px solid #ef444433", borderRadius:10, display:"flex", justifyContent:"space-between", fontSize:13 }}>
          <span style={{ color:"#ef4444" }}>❌ Perdidos</span>
          <span style={{ color:"#ef4444", fontWeight:700 }}>{fmt(dados.find(d=>d.id==="perdido").valor)}</span>
        </div>
      )}
    </div>
  );
}

// ── TABELA ───────────────────────────────────────────────
function GraficoTabela({ dados, titulo }) {
  const { theme } = useTheme();
  const total = dados.reduce((a,d)=>a+d.valor,0)||1;
  return (
    <div>
      <div style={{ fontSize:13, fontWeight:700, color:theme.text, marginBottom:16 }}>{titulo}</div>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr style={{ background:theme.bgInput }}>
            {["Etapa","Qtd","Valor","% do total","Barra"].map(h=>(
              <th key={h} style={{ padding:"8px 12px", fontSize:11, color:theme.textMuted, fontWeight:600, letterSpacing:".04em", textTransform:"uppercase", textAlign:"left", borderBottom:`1px solid ${theme.border}` }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dados.map((d,i)=>(
            <tr key={i} style={{ borderBottom:`1px solid ${theme.border}22` }}
              onMouseEnter={e=>e.currentTarget.style.background=theme.bgInput}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <td style={{ padding:"10px 12px" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:10, height:10, borderRadius:3, background:d.color }}/>
                  <span style={{ fontSize:13, color:theme.text, fontWeight:500 }}>{d.label}</span>
                </div>
              </td>
              <td style={{ padding:"10px 12px", fontSize:13, color:theme.textSub }}>{d.count}</td>
              <td style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:d.color }}>{fmt(d.valor)}</td>
              <td style={{ padding:"10px 12px", fontSize:13, color:theme.textMuted }}>{Math.round((d.valor/total)*100)}%</td>
              <td style={{ padding:"10px 12px", width:120 }}>
                <div style={{ background:theme.bgInput, borderRadius:99, height:8, overflow:"hidden" }}>
                  <div style={{ width:`${(d.valor/total)*100}%`, background:d.color, height:"100%", borderRadius:99 }}/>
                </div>
              </td>
            </tr>
          ))}
          <tr style={{ background:theme.bgInput }}>
            <td style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:theme.text }} colSpan={2}>Total</td>
            <td style={{ padding:"10px 12px", fontSize:13, fontWeight:800, color:ORANGE }} colSpan={3}>{fmt(dados.reduce((a,d)=>a+d.valor,0))}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── COMPONENTE PRINCIPAL ─────────────────────────────────
export default function Dashboard() {
  const { theme } = useTheme();
  const [data,    setData]    = useState(null);
  const [negData, setNegData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allLeads, setAllLeads] = useState([]);
  const [drill, setDrill] = useState(null);
  const [viewPipeline, setViewPipeline] = useState("barrasH");
  const [viewOrigem,   setViewOrigem]   = useState("pizza");
  const [viewKanban,   setViewKanban]   = useState("barrasH");

  useEffect(() => {
    Promise.all([
      api.get("/leads/metricas"),
      api.get("/leads?limit=1000&tipo=negociacao"),
      api.get("/leads?limit=2000"),
    ]).then(([metricasRes, negsRes, allRes]) => {
      setData(metricasRes.data);
      setNegData(negsRes.data.leads);
      const todos = allRes.data?.leads || allRes.data || [];
      setAllLeads(Array.isArray(todos) ? todos : []);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  if (loading) return <div style={{ color:theme.textMuted, padding:40, textAlign:"center", fontFamily:"'Inter',sans-serif" }}>Carregando...</div>;
  if (!data)   return <div style={{ color:"#ef4444", padding:40, textAlign:"center" }}>Erro ao carregar dados.</div>;

  const pipeline     = data.pipeline || [];
  const origens      = data.origens  || [];
  const mrrFechado   = pipeline.find(p=>p._id==="ganho")?.valor||0;
  const mrrPipeline  = pipeline.filter(p=>!["ganho","perdido"].includes(p._id)).reduce((a,p)=>a+p.valor,0);
  const totalLeads   = pipeline.reduce((a,p)=>a+p.count,0);
  const totalFechado = pipeline.find(p=>p._id==="ganho")?.count||0;
  const taxaConv = totalLeads ? Math.round((totalFechado/totalLeads)*100) : 0;

  // Dados de negociações por etapa
  const negPorEtapa = ETAPAS_PIPELINE.map(et=>({
    ...et,
    count: (negData||[]).filter(l=>l.stage===et.id).length,
    valor: (negData||[]).filter(l=>l.stage===et.id).reduce((a,l)=>a+Number(l.valor||0),0),
  }));

  // Dados kanban por etapa
  const kanbanPorEtapa = STAGES_KANBAN.map(s=>{
    const found = pipeline.find(p=>p._id===s.id)||{valor:0,count:0};
    return { ...s, valor:found.valor, count:found.count };
  });

  // Dados origens
  const origensDados = origens.slice(0,6).map((o,i)=>({
    label: o._id, valor: o.count, count: o.count,
    color: ["#6366f1","#FF6B2B","#10b981","#f59e0b","#3b82f6","#8b5cf6"][i%6]
  }));

  const VIEWS = [
    { id:"pizza",    label:"🍕 Pizza",  title:"Pizza"   },
    { id:"barrasH",  label:"📊 Barras", title:"Barras"  },
    { id:"barrasV",  label:"📈 Colunas",title:"Colunas" },
    { id:"funil",    label:"🔻 Funil",  title:"Funil"   },
    { id:"tabela",   label:"📋 Tabela", title:"Tabela"  },
  ];

  const ViewSelector = ({ value, onChange, exclude=[] }) => (
    <div style={{ display:"flex", background:theme.bgInput, borderRadius:10, padding:3, gap:2 }}>
      {VIEWS.filter(v=>!exclude.includes(v.id)).map(v=>(
        <button key={v.id} onClick={()=>onChange(v.id)} style={{ padding:"5px 10px", borderRadius:7, fontSize:11, fontWeight:value===v.id?700:400, cursor:"pointer", background:value===v.id?ORANGE:"transparent", border:"none", color:value===v.id?"var(--card-bg)":theme.textMuted, whiteSpace:"nowrap" }}>
          {v.label}
        </button>
      ))}
    </div>
  );

  const renderView = (view, dados, titulo, campo="valor") => {
    switch(view) {
      case "pizza":   return <GraficoPizza   dados={dados} titulo={titulo} campo={campo}/>;
      case "barrasH": return <GraficoBarrasH dados={dados} titulo={titulo} campo={campo}/>;
      case "barrasV": return <GraficoBarrasV dados={dados} titulo={titulo} campo={campo}/>;
      case "funil":   return <GraficoFunil   dados={dados} titulo={titulo}/>;
      case "tabela":  return <GraficoTabela  dados={dados} titulo={titulo}/>;
      default:        return <GraficoBarrasH dados={dados} titulo={titulo} campo={campo}/>;
    }
  };

  const Card=({children,style={}})=>(
    <div style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:20, ...style }}>{children}</div>
  );

  return (
    <div style={{ padding:24, fontFamily:"'Inter',sans-serif", background:theme.bg, minHeight:"100vh" }}>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ margin:0, fontWeight:800, fontSize:22, color:theme.text }}>Dashboard</h2>
        <p style={{ margin:"4px 0 0", fontSize:13, color:theme.textMuted }}>{"Visão geral" + (marcaSync().nome ? " · " + marcaSync().nome : "")}</p>
      </div>

      {/* Métricas gerais */}
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:20 }}>
        {[
          ["Ganho (fechado)", fmt(mrrFechado),  "#10b981","negócios ganhos"],
          ["Pipeline",       fmt(mrrPipeline), ORANGE,   "em negociação"],
          ["Total Leads",    totalLeads,        theme.text,"todos os estágios"],
          ["Taxa Conversão", `${taxaConv}%`,   "#6366f1",`${totalFechado} ganhos`],
        ].map(([label,val,color,sub],idx)=>(
          <div key={label} onClick={()=>setDrill(["mrr","pipeline","leads","conv"][idx])} title="Clique para ver os detalhes"
            onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 6px 18px rgba(16,24,40,.10)";}}
            onMouseLeave={e=>{e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none";}}
            style={{ background:theme.bgCard, border:`1px solid ${theme.border}`, borderRadius:12, padding:"16px 20px", flex:1, minWidth:130, cursor:"pointer", transition:"transform .12s, box-shadow .12s" }}>
            <div style={{ fontSize:11, color:theme.textMuted, letterSpacing:".04em", textTransform:"uppercase", marginBottom:6 }}>{label}</div>
            <div style={{ fontSize:26, fontWeight:800, color, fontFamily:"'Inter',sans-serif" }}>{val}</div>
            <div style={{ fontSize:11, color:theme.textMuted, marginTop:4 }}>{sub}</div>
          </div>
        ))}
      </div>

      {drill && <DrillModal tipo={drill} leads={allLeads} onFechar={()=>setDrill(null)} />}

      {/* Pipeline de negociações */}
      <Card style={{ marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:theme.text }}>💰 Pipeline de Negociações</div>
            <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>Prospecção → Ganho</div>
          </div>
          <ViewSelector value={viewPipeline} onChange={setViewPipeline}/>
        </div>
        {renderView(viewPipeline, negPorEtapa, "Pipeline por Etapa")}
      </Card>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
        {/* Origem dos leads */}
        <Card>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:theme.text }}>📍 Origem dos Leads</div>
            <ViewSelector value={viewOrigem} onChange={setViewOrigem} exclude={["funil"]}/>
          </div>
          {renderView(viewOrigem, origensDados, "Por Origem", "count")}
        </Card>

        {/* Pipeline Kanban */}
        <Card>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:theme.text }}>📋 Kanban por Etapa</div>
            <ViewSelector value={viewKanban} onChange={setViewKanban}/>
          </div>
          {renderView(viewKanban, kanbanPorEtapa, "Kanban por Etapa")}
        </Card>
      </div>

      {/* Meta */}
      <Card>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
          <div style={{ fontSize:13, fontWeight:700, color:theme.text }}>🎯 Meta Anual — 500 Clientes</div>
          <span style={{ color:"#10b981", fontWeight:700, fontSize:13 }}>{totalFechado}/500</span>
        </div>
        <div style={{ background:theme.bgInput, borderRadius:8, height:14, overflow:"hidden", marginBottom:8 }}>
          <div style={{ width:`${Math.min((totalFechado/500)*100,100)}%`, background:`linear-gradient(90deg,${ORANGE},#10b981)`, height:"100%", borderRadius:8, transition:"width .5s" }}/>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:theme.textMuted }}>
          <span>0</span>
          <span style={{ color:ORANGE, fontWeight:600 }}>{Math.round((totalFechado/500)*100)}% da meta</span>
          <span>500 clientes × R$1.200 = {fmt(600000)}/mês</span>
        </div>
      </Card>
    </div>
  );
}

const DRILL_CFG = {
  mrr:      { titulo:"Ganho — negócios fechados", cor:"#10b981", filtro:(l)=>l.stage==="ganho" },
  pipeline: { titulo:"Pipeline — em negociação",      cor:"#FF6B2B", filtro:(l)=>!["ganho","perdido"].includes(l.stage) },
  leads:    { titulo:"Total de Leads",                cor:"#6366f1", filtro:()=>true },
  conv:     { titulo:"Convertidos — ganhos",        cor:"#10b981", filtro:(l)=>l.stage==="ganho" },
};

function DrillModal({ tipo, leads, onFechar }) {
  const cfg = DRILL_CFG[tipo] || DRILL_CFG.leads;
  const lista = (leads||[]).filter(cfg.filtro).sort((a,b)=>Number(b.valor||0)-Number(a.valor||0));
  const total = lista.reduce((a,l)=>a+Number(l.valor||0),0);
  const th = { padding:"9px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.4, borderBottom:"1px solid var(--border-color)", whiteSpace:"nowrap" };
  const td = { padding:"9px 12px", fontSize:13, color:"var(--text-secondary)", borderBottom:"1px solid var(--border-light)" };
  const brl = (v)=>`R$${Number(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2})}`;

  return (
    <div onClick={onFechar} style={{ position:"fixed", inset:0, background:"rgba(16,24,40,.55)", display:"flex", alignItems:"flex-start", justifyContent:"center", zIndex:1000, padding:"40px 16px", overflow:"auto" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"var(--card-bg)", borderRadius:12, width:"100%", maxWidth:1000, padding:24, boxShadow:"0 20px 60px rgba(0,0,0,.25)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
          <h2 style={{ margin:0, fontSize:18, fontWeight:800, color:"var(--text-primary)" }}>{cfg.titulo}</h2>
          <button onClick={onFechar} style={{ border:"none", background:"none", fontSize:22, color:"#98A2B3", cursor:"pointer", lineHeight:1 }}>×</button>
        </div>
        <div style={{ fontSize:13, color:"var(--text-secondary)", marginBottom:16 }}>
          <strong style={{ color:cfg.cor, fontSize:16 }}>{lista.length}</strong> registros
          {" · "}total <strong style={{ color:cfg.cor, fontSize:16 }}>{brl(total)}</strong>
        </div>

        <div style={{ border:"1px solid var(--border-color)", borderRadius:8, overflow:"auto", maxHeight:"60vh" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead style={{ position:"sticky", top:0, background:"var(--page-bg)" }}>
              <tr>
                <th style={{...th, width:36, textAlign:"center"}}>#</th>
                <th style={th}>Nome</th>
                <th style={th}>Empresa</th>
                <th style={th}>Serviço</th>
                <th style={th}>Etapa</th>
                <th style={th}>Responsável</th>
                <th style={{...th, textAlign:"right"}}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((l,i)=>(
                <tr key={l._id||i}>
                  <td style={{...td, textAlign:"center", color:"#98A2B3"}}>{i+1}</td>
                  <td style={{...td, fontWeight:600, color:"var(--text-primary)"}}>{l.nome||"—"}</td>
                  <td style={td}>{l.nomeCliente||l.empresa||"—"}</td>
                  <td style={td}>{l.servico||"—"}</td>
                  <td style={td}>
                    <span style={{ display:"inline-block", padding:"2px 9px", borderRadius:10, fontSize:11, fontWeight:600, color:cfg.cor, background:cfg.cor+"1A" }}>
                      {l.stage||"—"}
                    </span>
                  </td>
                  <td style={td}>{l.responsavelNome||l.nomeResponsavel||l.responsavel?.nome||"—"}</td>
                  <td style={{...td, textAlign:"right", fontWeight:700, color:"#1D9E75"}}>{brl(l.valor)}</td>
                </tr>
              ))}
              {!lista.length && (
                <tr><td colSpan={7} style={{...td, textAlign:"center", color:"#98A2B3", padding:30}}>Nenhum registro.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
